import { useState, useEffect, useMemo } from "react";
import styles from "../styles/theme";
import TechCard from "../components/TechCard";
import { supabase } from "../supabaseClient";
import { hasAnyCertExpired, calcBestMatch, calcJobMatch, normalizeJob } from "../utils/helpers";

export default function TechsView({ techs, setActiveTech, goToCreateProfile, user, myProfile, navigate, accountType, isPaidEmployer }) {
  const [hideExpired, setHideExpired] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [logbookOnly, setLogbookOnly] = useState(false);
  const [employerJobs, setEmployerJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('all');

  const filteredTechs = techs
    .filter(t => !hideExpired || !hasAnyCertExpired(t))
    .filter(t => !verifiedOnly || t.logbookVerificationStatus === 'verified')
    .filter(t => !logbookOnly || t.hasLogbook);
  const isFreeEmployer = accountType === 'employer' && !isPaidEmployer;
  const isEmployer = accountType === 'employer';

  // Fetch employer's active job posts once when Browse Techs loads
  // Re-run when accountType changes since it loads asynchronously
  useEffect(() => {
    if (!isEmployer || !user) { setEmployerJobs([]); return; }
    supabase.from('job_posts').select('*').eq('user_id', user.id).eq('status', 'published')
      .then(({ data, error }) => {
        if (error) { setEmployerJobs([]); return; }
        setEmployerJobs((data || []).map(normalizeJob));
      });
  }, [isEmployer, user]); // eslint-disable-line

  const selectedJob = selectedJobId !== 'all'
    ? employerJobs.find(j => j.id === selectedJobId) || null
    : null;

  // Pre-calculate matches for all techs
  const techMatches = useMemo(() => {
    if (employerJobs.length === 0) return {};
    const map = {};
    filteredTechs.forEach(tech => {
      if (selectedJob) {
        // Single job selected — wrap in the same shape as calcBestMatch
        const match = calcJobMatch(tech, selectedJob);
        map[tech.id] = { best: match, all: [match] };
      } else {
        map[tech.id] = calcBestMatch(tech, employerJobs);
      }
    });
    return map;
  }, [filteredTechs, employerJobs, selectedJob]);

  // Sort techs: Best Fit uses job-based matching when employer has jobs
  const sortedTechs = useMemo(() => {
    if (employerJobs.length === 0) return filteredTechs;
    return [...filteredTechs].sort((a, b) => {
      const ma = techMatches[a.id]?.best;
      const mb = techMatches[b.id]?.best;
      // Techs meeting all requirements first
      const aReq = ma?.meetsAllRequired ? 1 : 0;
      const bReq = mb?.meetsAllRequired ? 1 : 0;
      if (aReq !== bReq) return bReq - aReq;
      // Then by preferred match pct
      const aPref = ma?.preferredPct || 0;
      const bPref = mb?.preferredPct || 0;
      if (aPref !== bPref) return bPref - aPref;
      // Fallback: total certs then hours
      const aCerts = (a.dodCerts?.length || 0);
      const bCerts = (b.dodCerts?.length || 0);
      if (aCerts !== bCerts) return bCerts - aCerts;
      return parseInt(b.uxoHours || '0') - parseInt(a.uxoHours || '0');
    });
  }, [filteredTechs, employerJobs, techMatches]);

  return (
    <div>
      <div style={styles.pageHeader}>
        <h2 style={styles.pageTitle} data-page-title>Available UXO Technicians</h2>
        <p style={styles.pageSub} data-page-subtitle>
          Certified techs actively open to new assignments.{' '}
          {user && myProfile ? (
            <a href="#" style={styles.inlineLink} onClick={e => { e.preventDefault(); navigate('/dashboard'); }}>
              Go to Dashboard {"\u2192"}
            </a>
          ) : user && accountType === 'employer' ? (
            <a href="#" style={styles.inlineLink} onClick={e => { e.preventDefault(); navigate('/employer-dashboard'); }}>
              Go to Employer Hub {"\u2192"}
            </a>
          ) : (
            <a href="#" style={styles.inlineLink} onClick={e => { e.preventDefault(); goToCreateProfile(); }}>
              Create your profile {"\u2192"}
            </a>
          )}
        </p>
      </div>

      {isFreeEmployer && (
        <div style={{
          background: '#1a1408', border: '1px solid #d97706', borderRadius: 8,
          padding: '14px 20px', marginBottom: 20, display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <div style={{ color: '#d97706', fontWeight: 'bold', fontSize: 14 }}>
              {"\uD83D\uDD12"} Upgrade to view full profiles and contact information
            </div>
            <div style={{ color: '#9a9490', fontSize: 12, marginTop: 2 }}>
              Free accounts can see name, role, certifications, and general region only.
            </div>
          </div>
          <button style={{
            background: '#d97706', border: 'none', color: '#0d0f10', padding: '8px 18px',
            fontSize: 13, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
            flexShrink: 0,
          }} onClick={() => navigate('/employer-dashboard', { state: { openTab: 'account' } })}>
            Upgrade Now
          </button>
        </div>
      )}

      {/* Filter bar — employers only */}
      {isEmployer && (
        <div style={styles.filterBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flex: 1 }}>
            {/* Job filter dropdown */}
            {employerJobs.length > 0 && (
              <select
                style={styles.filterSelect}
                value={selectedJobId}
                onChange={e => setSelectedJobId(e.target.value)}
              >
                <option value="all">Best match across all jobs</option>
                {employerJobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            )}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              fontSize: 13, color: '#9a9490', userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={hideExpired}
                onChange={e => setHideExpired(e.target.checked)}
                style={{ accentColor: '#d97706', width: 16, height: 16, cursor: 'pointer' }}
              />
              Hide expired certs
            </label>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              fontSize: 13, color: '#9a9490', userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={e => setVerifiedOnly(e.target.checked)}
                style={{ accentColor: '#d97706', width: 16, height: 16, cursor: 'pointer' }}
              />
              Documented hours only
            </label>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              fontSize: 13, color: '#9a9490', userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={logbookOnly}
                onChange={e => setLogbookOnly(e.target.checked)}
                style={{ accentColor: '#d97706', width: 16, height: 16, cursor: 'pointer' }}
              />
              Logbook available
            </label>
          </div>
          <span style={styles.filterCount}>
            {sortedTechs.length} tech{sortedTechs.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      <div style={styles.cardGrid} data-card-grid>
        {sortedTechs.map(tech => (
          <TechCard key={tech.id} tech={tech} onClick={() => setActiveTech(tech)}
            isFreeEmployer={isFreeEmployer}
            matchData={techMatches[tech.id] || null} />
        ))}
      </div>
    </div>
  );
}
